// Emotional-timing paywall triggers. All state lives client-side in localStorage.
import { useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/useCredits";
import { supabase } from "@/integrations/supabase/client";

export type PaywallVariant = "hype" | "fomo" | "urgency" | "upsell249";

interface PaywallState {
  tryonsDone: number;
  paidCreditsSpent: number;
  hypeShownAt: number | null;
  hypeDismissed: boolean;
  fomoShown: boolean;
  upsell249Shown: boolean;
  lastPurchaseAt: number | null;
  creditsPurchasedTotal: number;
  sessionShown: boolean;
}

const DEFAULTS: PaywallState = {
  tryonsDone: 0,
  paidCreditsSpent: 0,
  hypeShownAt: null,
  hypeDismissed: false,
  fomoShown: false,
  upsell249Shown: false,
  lastPurchaseAt: null,
  creditsPurchasedTotal: 0,
  sessionShown: false,
};

const keyFor = (uid: string) => `paywall_state_${uid}`;

/**
 * Wipe all paywall-related client state for a user. Call after account deletion
 * so a fresh signup with the same browser doesn't inherit stale "shown" flags.
 */
export const clearPaywallState = (uid: string) => {
  try {
    localStorage.removeItem(keyFor(uid));
    // Sweep per-tryon "seen" markers so future try-ons can re-trigger.
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith("paywall_seen_")) toRemove.push(k);
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
    sessionShown = false;
  } catch {
    /* ignore */
  }
};

const read = (uid: string): PaywallState => {
  try {
    const raw = localStorage.getItem(keyFor(uid));
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...(JSON.parse(raw) as PaywallState) };
  } catch {
    return { ...DEFAULTS };
  }
};

const write = (uid: string, s: PaywallState) => {
  try {
    localStorage.setItem(keyFor(uid), JSON.stringify(s));
  } catch {
    /* ignore */
  }
};

// In-memory flag so we don't show 2 modals in the same JS session even if
// localStorage hasn't been flushed across tabs.
let sessionShown = false;

export const usePaywallTriggers = () => {
  const { user } = useAuth();
  const { credits } = useCredits();
  const creditsRef = useRef<number | null>(credits);
  creditsRef.current = credits;

  const uid = user?.id ?? null;

  const canShow = useCallback(() => {
    if (!uid) return false;
    if (sessionShown) return false;
    const s = read(uid);
    if (s.sessionShown) return false;
    // Skip nag if user purchased in the last 10 minutes.
    if (s.lastPurchaseAt && Date.now() - s.lastPurchaseAt < 10 * 60 * 1000) return false;
    return true;
  }, [uid]);

  const markShown = useCallback(() => {
    if (!uid) return;
    sessionShown = true;
    const s = read(uid);
    write(uid, { ...s, sessionShown: true });
  }, [uid]);

  /** Called on Result.tsx when a successful generation loads. */
  const checkAfterTryon = useCallback(
    (tryon: { fit_score: number | null; id: string } | null): { variant: PaywallVariant } | null => {
      if (!uid || !tryon) return null;
      const s = read(uid);
      // Track lifetime tryons (dedupe via a marker on the id).
      const seenKey = `paywall_seen_${tryon.id}`;
      if (!sessionStorage.getItem(seenKey)) {
        sessionStorage.setItem(seenKey, "1");
        s.tryonsDone += 1;
        // If user has made any paid purchase, count toward paidCreditsSpent.
        if (s.creditsPurchasedTotal > 0) s.paidCreditsSpent += 1;
        write(uid, s);
      }

      if (!canShow()) return null;

      // upsell249 eligibility — already-converted users who spent 3+ paid credits.
      if (s.creditsPurchasedTotal > 0 && s.paidCreditsSpent >= 3 && !s.upsell249Shown) {
        write(uid, { ...s, upsell249Shown: true });
        markShown();
        return { variant: "upsell249" };
      }

      // hype — 2nd+ try-on with a strong fit_score.
      const score = tryon.fit_score ?? 0;
      if (
        !s.hypeShownAt &&
        !s.hypeDismissed &&
        s.tryonsDone >= 2 &&
        score >= 80 &&
        s.creditsPurchasedTotal === 0
      ) {
        write(uid, { ...s, hypeShownAt: Date.now() });
        markShown();
        return { variant: "hype" };
      }

      return null;
    },
    [uid, canShow, markShown],
  );

  /** Called before kicking off a generation — returns urgency/fomo if eligible. */
  const checkBeforeTryon = useCallback((): { variant: PaywallVariant } | null => {
    if (!uid) return null;
    const s = read(uid);
    const bal = creditsRef.current ?? 0;

    // urgency — 1 or 0 credits left, never-purchased user. This bypasses the
    // session-once gate because it's a hard wall, not a nag.
    if (bal <= 1 && s.creditsPurchasedTotal === 0) {
      markShown();
      return { variant: "urgency" };
    }

    if (!canShow()) return null;

    // fomo — dismissed hype, trying again.
    if (s.hypeDismissed && !s.fomoShown && s.creditsPurchasedTotal === 0) {
      write(uid, { ...s, fomoShown: true });
      markShown();
      return { variant: "fomo" };
    }

    return null;
  }, [uid, canShow, markShown]);

  /** Called on favorite / share actions. */
  const checkAfterAction = useCallback(
    (_kind: "favorite" | "share"): { variant: PaywallVariant } | null => {
      if (!uid) return null;
      const s = read(uid);
      if (!canShow()) return null;
      if (s.creditsPurchasedTotal === 0) return null;
      if (s.upsell249Shown) return null;
      write(uid, { ...s, upsell249Shown: true });
      markShown();
      return { variant: "upsell249" };
    },
    [uid, canShow, markShown],
  );

  /** Called when the hype modal is dismissed without purchasing. */
  const markHypeDismissed = useCallback(() => {
    if (!uid) return;
    const s = read(uid);
    write(uid, { ...s, hypeDismissed: true });
  }, [uid]);

  /** Called after a successful purchase. */
  const markPurchased = useCallback(
    async (creditsAdded: number) => {
      if (!uid) return;
      const s = read(uid);
      write(uid, {
        ...s,
        lastPurchaseAt: Date.now(),
        creditsPurchasedTotal: s.creditsPurchasedTotal + creditsAdded,
        paidCreditsSpent: 0, // reset after purchase; count fresh
        sessionShown: false, // allow a fresh upsell later in a new session
      });
      sessionShown = false;

      // Also pull server-side authoritative total in case purchases happened in another tab.
      try {
        const { data } = await supabase
          .from("profiles")
          .select("credits_purchased_total")
          .eq("user_id", uid)
          .maybeSingle();
        if (typeof data?.credits_purchased_total === "number") {
          const s2 = read(uid);
          write(uid, { ...s2, creditsPurchasedTotal: data.credits_purchased_total });
        }
      } catch {
        /* ignore */
      }
    },
    [uid],
  );

  return {
    checkAfterTryon,
    checkBeforeTryon,
    checkAfterAction,
    markHypeDismissed,
    markPurchased,
  };
};
